/**
 * The **verifying** state (§ 4.5) — the screen that exists because a transport
 * fault breaks the *reply*, not the request.
 *
 * When a post has been attempted and no terminal outcome is recorded, the honest
 * statement is *"we do not know yet whether this posted"*, and the only safe action
 * is to **ask Business Central**. So this screen polls `GET …/posting`.
 *
 * **The post request is never retried, here or anywhere.** A resend after a broken
 * reply can post the count twice, and inventory moved twice is a real accounting
 * problem someone has to unpick by hand. Reading is safe and repeatable; that
 * asymmetry is what makes this state possible at all.
 *
 * Reached two ways, and it must be both:
 *
 * 1. The post attempt fails at the transport in this session of the app.
 * 2. **The app is reopened** with `post_attempted_at` set and no terminal outcome —
 *    after the tab was killed, the iPad slept, or the manager force-quit. Rule 4 of
 *    § 4.5, and the case that makes the marker worth persisting rather than holding
 *    in memory.
 *
 * ## The way out, RULED 2026-08-24
 *
 * D6 shipped this screen with one state it could only report: a lost reply over a
 * session Business Central still reads as `InReview`. Not terminal, not stuck, and —
 * as the app had it then — not resolvable, so the screen said to contact Copeland
 * and the cost was a phone call on a Friday with a counted shelf and no way to post.
 *
 * Copeland's ruling closes it. `PostSession` marks a session `Posting` **as its first
 * act**, so `InReview` on two consecutive polls more than a minute after the attempt
 * means the request never arrived: nothing was staged for posting, nothing is in the
 * ledger, and the count is simply un-posted. The screen then says so and **puts the
 * confirm back**. The rule itself is `posting/neverStarted.ts`, pure and mirrored in
 * `count/posting.py`; this screen only renders what it decides.
 *
 * Three things keep that from becoming a retry in disguise:
 *
 * - **Nothing is re-sent.** The lost request is gone. What comes back is the confirm
 *   modal over a review payload read live a moment ago — a *new* post, made by a
 *   manager who has just been told the first one never arrived.
 * - **The same one gesture.** `review/ConfirmPost.tsx` is still the only component in
 *   the app that can produce `confirm: true`, and this screen renders it rather than
 *   growing a Post button of its own.
 * - **`presumed_stuck` is untouched.** A post that *started* and stalled keeps D-5's
 *   rule exactly: report it, contact Copeland, never re-offer. The whole ruling turns
 *   on the difference between a post that never began and one that did.
 */
import { Link } from "react-router-dom";

import { usePostingStatus, useSessionReview } from "../api/queries";
import { PostingOutcome } from "../posting/PostingOutcome";
import { mayReofferPost, useNeverStartedTally } from "../posting/neverStarted";
import { ConfirmPost } from "../review/ConfirmPost";
import { formatTimestamp } from "../sessions/format";
import type { SessionScreenProps } from "./SessionRoute";

export function Verifying({ session, mine, areaId, postAttemptedAt }: SessionScreenProps & {
  postAttemptedAt: string | null;
}) {
  const poll = usePostingStatus(areaId, session.session_id, true);
  const status = poll.data;

  // Counted off react-query's own stamps, not off the data: two consecutive polls
  // that both say `InReview` are two *identical* answers, and a hook watching the
  // value would see one reading no matter how long it went on.
  const tally = useNeverStartedTally({
    postAttemptedAt,
    status: status?.status,
    dataUpdatedAt: poll.dataUpdatedAt,
    errorUpdatedAt: poll.errorUpdatedAt,
  });
  const reoffer = mayReofferPost(tally);

  // Read only once the rule has fired. The review payload is **never cached**
  // (§ 4.6) — on-hand and unit cost are session-time facts — so fetching it before
  // there is anything to confirm would be a request whose answer expires unused, and
  // the `expected_line_count` this posts under has to be the current one anyway.
  const review = useSessionReview(areaId, session.session_id, reoffer);

  return (
    <section data-testid="verifying-screen">
      <h2>{session.area.display_name} — checking Business Central</h2>

      {reoffer ? (
        <div className="card notice" data-testid="never-started-notice">
          <p>
            <strong>This count never reached Business Central.</strong> It was sent at{" "}
            {formatTimestamp(postAttemptedAt)} and the reply never arrived — but
            Business Central has now shown this count as <code>{session.status}</code>{" "}
            on two checks more than a minute later, and it marks a count as posting the
            instant a request lands. So the request never got there.
          </p>
          <p>
            <strong>Nothing has posted, and nothing is being re-sent.</strong> That
            attempt is gone. The count is exactly as it was before it — you can post it
            below, and it will be the first time it posts.
          </p>
        </div>
      ) : (
        <div className="card notice" data-testid="verifying-notice">
          <p>
            <strong>We don't know yet whether this count posted.</strong> It was sent at{" "}
            {formatTimestamp(postAttemptedAt)} and the reply never arrived — which can
            mean it posted, or that it never got there. Business Central is being asked
            now.
          </p>
          <p>
            <strong>Do not post it again.</strong> Posting a count twice moves inventory
            twice, and this app will not offer to do it until Business Central has said
            what happened.
          </p>
        </div>
      )}

      {poll.isLoading && <p className="muted">Asking Business Central…</p>}

      {poll.error && (
        <div className="card error">
          <p>Could not reach Business Central: {(poll.error as Error).message}</p>
          <p className="muted small">
            The count's fate is unchanged by this — it is whatever BC already holds.
            This screen keeps asking; nothing is re-sent.
          </p>
        </div>
      )}

      {status && (
        <>
          <p className="muted small" data-testid="verifying-answer">
            Business Central says: <strong>{status.status}</strong>
            {typeof status.elapsed_seconds === "number" && (
              <> · posting for {Math.round(status.elapsed_seconds)}s</>
            )}
          </p>
          {/* The same four outcomes S7 renders, from the same component. Whatever
              Business Central says, it means the same thing however the app got
              here — and describing it twice is how two screens come to disagree. */}
          <PostingOutcome
            status={status}
            sessionId={session.session_id}
            reoffer={reoffer ? "offered" : "watching"}
          />
        </>
      )}

      {reoffer && (
        <>
          {review.isLoading && (
            <p className="muted">Reading what this count would post…</p>
          )}
          {review.error && (
            <div className="card error" data-testid="reoffer-review-error">
              <p>Could not read the review: {(review.error as Error).message}</p>
              <p className="muted small">
                {/* No payload, no line count — and `expected_line_count` is what makes
                    "what posts is what was reviewed" true. Offering the post without
                    it would be offering a post nobody has reviewed. */}
                The post is not offered without it: what posts has to be what was
                reviewed, and that is the figure this screen is missing.
              </p>
            </div>
          )}
          {review.data && (
            <ConfirmPost
              session={session}
              areaId={areaId}
              mine={mine}
              review={review.data}
              neverStarted
            />
          )}
        </>
      )}

      <p>
        <Link to={`/areas/${encodeURIComponent(areaId)}`}>← Back to this area's counts</Link>
      </p>
    </section>
  );
}
